<template>
  <popup-modal
    :title="title"
    portal="selects"
    @close="close"
    @scroll="scroll"
  >
    <search-bar
      :autofocus="desktop"
      :value="searching"
      class="former-select-popup-search"
      @input="search"
    />

    <ul
      ref="options"
      class="former-select-popup-options"
    >
      <li
        v-for="option in filtered"
        :key="option.value"
        :class="classes(option)"
        tabindex="0"
        @click="select(option)"
        @keydown.enter="select(option)"
      >
        <span class="former-select-popup-option-label">
          {{ option.label }}
        </span>

        <font-awesome-icon
          class="former-select-popup-option-arrow"
          icon="chevron-right"
        />
      </li>

      <state-messages
        key="proposal-states"
        class="former-select-popup-state"
        :state="currentState"
      >
        <template v-slot:loading>
          Carregando opções...
        </template>

        <template v-slot:error>
          Algo deu errado enquanto tentávamos carregar as opções.
          Por favor, tente novamente.
        </template>

        <template v-slot:empty>
          Nenhum resultado encontrado
        </template>
      </state-messages>
    </ul>
  </popup-modal>
</template>

<script>
  import { prepare } from '@cordel/helpers/form/select/options';

  import PopupModal from '@/components/Popup/Modal.vue';
  import SearchBar from '@/components/Search/Bar.vue';
  import StateMessages from '@/components/State/Messages.vue';

  export default {
    name: 'FormerSelect',
    components: {
      SearchBar,
      StateMessages,
      PopupModal,
    },
    props: {
      options: {
        type: Array,
        default() {
          return [];
        },
      },
      processing: {
        type: Boolean,
        default: false,
      },
      local: {
        type: Boolean,
        default: false,
      },
      desktop: {
        type: Boolean,
        default: false,
      },
      title: {
        type: String,
        default: 'Selecione',
      },
    },
    data() {
      return {
        searching: '',
      };
    },
    computed: {
      filtered() {
        const {
          local,
          options,
          searching,
        } = this;

        if (!local || !searching) {
          return options;
        }

        const query = prepare(searching);

        return options.filter(option => prepare(option.label || '').includes(query));
      },

      currentState() {
        const {
          filtered,
          processing,
        } = this;

        if (processing) {
          return 'loading';
        }

        return filtered.length ? 'ready' : 'empty';
      },
    },
    methods: {
      search(value) {
        this.searching = value;

        this.$emit('search', this.searching);
      },
      scroll(e) {
        const {
          options,
        } = this.$refs;

        if (options) {
          const {
            offsetHeight,
            scrollTop,
          } = e.target;

          if (scrollTop + offsetHeight + 64 > options.offsetHeight) {
            this.$emit('search', this.searching, true);
          }
        }
      },
      close() {
        this.$emit('close');
      },
      select(option) {
        if (option.disabled) {
          return;
        }

        this.$emit('select', option);
      },
      classes(option) {
        const classes = ['former-select-popup-option'];

        if (option.disabled) {
          classes.push('former-select-popup-option-disabled');
        }

        if (option.classes) {
          classes.push(option.classes);
        }

        return classes;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/_variables.scss';

  .former-select-popup-search {
    margin: 0 $gutter-small;

    @media all and (min-width: $breakpoint-desktop) {
      margin: 0 $gutter-bigger - $gutter-small;
    }
  }

  .former-select-popup-content {
    background-color: $white;
    margin: 0 auto;

    @media all and (min-width: $breakpoint-desktop) {
      box-shadow: 0 $gutter $gutter (-$gutter) $shadow;
      max-width: 480px;
    }
  }

  .former-select-popup-options {
    list-style: none;
    overflow: hidden;
  }

  .former-select-popup-option {
    align-items: center;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    outline: none;
    padding: $gutter-big * 0.5 $gutter;

    @media all and (min-width: $breakpoint-desktop) {
      padding-left: $gutter-bigger;
      padding-right: $gutter-bigger;
    }

    &:first-child {
      margin-top: $gutter-big * 0.5;
    }

    &:nth-last-child(2) {
      margin-bottom: $gutter-big * 0.5;
    }

    &:focus {
      background-color: $gray-light;
    }
  }

  .former-select-popup-option-disabled {
    color: $gray;
    cursor: default;

    &:focus {
      background-color: transparent;
    }
  }

  .former-select-popup-option-label {
    flex: 1;
    margin-right: $gutter;
  }

  .former-select-popup-option-arrow {
    color: $gray;
    font-size: $font-small;

    .former-select-popup-option-disabled & {
      display: none;
    }

    @media all and (min-width: $breakpoint-desktop) {
      .former-select-popup-option:hover & {
        color: $black;
      }
    }
  }
</style>
